/* eslint-disable max-len */
import React, {
    useState,
    useEffect,
    useMemo,
    useContext,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    Button,
    ModalNext,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalNextProvider,
} from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { ViewModelForm, useDataRefresh, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { DocumentComponent } from 'gw-capability-document-react';
import { useValidation } from 'gw-portals-validation-react';
import { useAuthentication } from 'gw-digital-auth-react';
import { FileUtil } from 'gw-portals-util-js';
import { ServiceRequestService } from 'gw-capability-servicerequest';
import documentMessages from 'gw-capability-document-react/Document.messages';
import messages from './AddQuoteModal.messages';
import metadata from './AddQuoteModal.metadata.json5';

function AddQuoteModal(props) {
    const [quoteVM, setQuoteVM] = useState({});
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const [isLoading, setLoadingState] = useState(false);
    const [showVatExempt, setVatExempt] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [lineItemVatData, setLineItemVatData] = useState([]);
    const [totalInclAmount, setTotalInclAmount] = useState(Number(0));
    const [showDocumentReqMsg, setShowDocumentReqMsg] = useState(false);
    const [docNumberPrev, setDocNumberPrev] = useState(Number(0))
    const { refreshData } = useDataRefresh();
    const { authHeader } = useAuthentication();
    const { isComponentValid, onValidate, registerComponentValidation } = useValidation('AddQuoteModal');
    const {
        isOpen,
        onReject,
        onResolve,
        quoteValue,
        onAfterClose,
        serviceRequestID,
        requestKind,
        vatCustomer,
        existingDocuments,
        isAddQuote,
        // eslint-disable-next-line react/prop-types
        serviceRequestDocumentType
    } = props;

    const quoteAlreadyExists = useMemo(() => {
        let condition = !_.isEmpty(quoteValue);
        condition = condition && !_.isNil(_.get(quoteValue, 'referenceNumber'));
        return condition;
    }, [quoteValue]);

    const handleDocReqMsg = useCallback(() => {
        if (quoteAlreadyExists) {
            setShowDocumentReqMsg(true);
        } else { setShowDocumentReqMsg(false); }
    }, [quoteAlreadyExists]);

    useEffect(() => {
        if (quoteAlreadyExists && quoteValue.documents) {
            quoteValue.existingDocumentsToLink = quoteValue.documents.map(
                (document) => document.publicID
            );
            const val = _.get(quoteVM, 'documents.value', []);
            setDocNumberPrev(val.length);
        }
        if (quoteAlreadyExists) {
            quoteValue.vat_Excl_EXT = '';
            quoteValue.description_EXT = '';
            quoteValue.vat_EXT = '';
            quoteValue.vat_incl_EXT = '';
            setTotalInclAmount(Number(0));
            setLineItemVatData([]);
        }
        handleDocReqMsg();
        setQuoteVM(
            viewModelService.create(
                isAddQuote ? {} : quoteValue,
                'cc',
                'edge.capabilities.servicerequest.statement.quote.dto.ServiceRequestQuoteDTO'
            )
        );
        // only execute once
    }, [handleDocReqMsg, isAddQuote, quoteAlreadyExists, quoteVM, quoteValue, viewModelService]);

    const fileMimeTypeMap = {
        csv: 'application/csv',
        doc: 'application/msword',
        msg: 'application/vnd.ms-outlook',
        eml: 'message/rfc822',
        zip: 'application/x-zip-compressed',
        pdf: 'application/pdf',
        ps: 'application/postscript',
        rtf: 'application/rtf',
        xls: 'application/vnd.ms-excel',
        ppt: 'application/vnd.ms-powerpoint',
        pps: 'application/vnd.ms-powerpoint',
        pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        wav: 'audio/wav',
        wma: 'audio/x-ms-wma',
        bmp: 'image/bmp',
        gif: 'image/gif',
        jpg: 'image/jpeg',
        tiff: 'image/tiff',
        tif: 'image/tiff',
        mdi: 'image/vnd.ms-modi',
        png: 'image/png',
        html: 'text/html',
        htm: 'text/html',
        txt: 'text/plain',
        rtx: 'text/richtext',
        xml: 'text/xml',
        mpg: 'video/mpeg',
        mpeg: 'video/mpeg',
        mov: 'video/quicktime',
        avi: 'video/x-msvideo',
        mp4: 'video/mp4'
    };

    const getCurrencyValue = useCallback((rowData, index, property) => {
        // eslint-disable-next-line prefer-template
        var value = rowData[property.id];
        var num = isNaN(value) ? 0 : Number(value);
        return 'R' + num.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    }, []);

    const calculateEXclVatAmount = useCallback((value1) => {
        let total;
        if (requestKind === 'salvage' || (vatCustomer && !showVatExempt)) {
            const upper = Number(value1) * Number(100);
            const exclValue = upper / Number(115);
            total = parseFloat(exclValue).toFixed(2);
        } else {
            total = value1;
        }
        const inclusiveVatMount = {
            amount: total,
            currency: 'rand'
        };
        return inclusiveVatMount;
    }, [requestKind, showVatExempt, vatCustomer]);

    const calculateEXclVatAmountForExempt = useCallback((value1, value) => {
        let total;
        if (requestKind === 'salvage' || (vatCustomer && !value)) {
            const upper = Number(value1) * Number(100);
            const exclValue = upper / Number(115);
            total = parseFloat(exclValue).toFixed(2);
        } else {
            total = value1;
        }
        const inclusiveVatMount = {
            amount: total,
            currency: 'rand'
        };
        return inclusiveVatMount;
    }, [requestKind, vatCustomer]);

    const calculateVatAmount = useCallback((value1, value2) => {
        const vat = parseFloat(Number(value1) - Number(value2)).toFixed(2);

        const vatAmount = {
            amount: vat,
            currency: 'rand'
        };
        return vatAmount;
    }, []);

    const calculateVat = useCallback((value1, value2) => {
        const total = Number(value2) - Number(value1);
        const exclusiveVatMount = {
            amount: total,
            currency: 'rand'
        };
        return exclusiveVatMount;
    }, []);

    const currencyFormat = (value) => {
        var num = isNaN(value) ? 0 : Number(value);
        return 'R' + num.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    };

    const clearLineItemData = useCallback(() => {
        _.set(quoteVM, 'description_EXT.value', undefined);
        _.set(quoteVM, 'vat_Excl_EXT.value', undefined);
        _.set(quoteVM, 'vat_EXT.value', undefined);
        _.set(quoteVM, 'vat_incl_EXT.value', undefined);
        _.set(quoteVM, 'vATExempt.value', undefined);
        setVatExempt(false);
    }, [quoteVM]);

    const addNewLineItem = useCallback(() => {
        const description = _.get(quoteVM, 'description_EXT.value');
        const vatExclAmount = _.get(quoteVM, 'vat_Excl_EXT.amount.value', 0);
        const vatAmount = _.get(quoteVM, 'vat_EXT.amount.value', 0);
        // const vatInclAmount = currencyFormat(_.get(quoteVM, 'vat_incl_EXT.amount.value', 0));
        const vatInclAmount = _.get(quoteVM, 'vat_incl_EXT.amount.value', 0).toFixed(2);
        if (description && vatInclAmount > 0 && vatExclAmount > 0) {
            const lineItem = {
                description, vatExclAmount, vatAmount, vatInclAmount
            };
            let total = Number(totalInclAmount) + Number(vatInclAmount);
            total = +total.toFixed(2);
            setTotalInclAmount(total);
            lineItemVatData.push(lineItem);
            _.set(quoteVM.value, 'totalInclAmount', totalInclAmount);
            _.set(quoteVM, 'lineItemData_EXT.value', lineItemVatData);
            clearLineItemData();
            refreshData();
        } else {
            ModalNextProvider.showAlert({
                title: 'Error - Mandatory Field validation failed',
                message: 'All fields are mandatory to Add Line',
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: 'Ok'
            }).catch(_.noop);
            return false;
        }

    }, [quoteVM, totalInclAmount, lineItemVatData, clearLineItemData, refreshData]);

    const writeValue = useCallback(
        (value, path) => {
            _.set(quoteVM, path, value);
            if (path === 'vat_incl_EXT') {
                if (!_.isUndefined(value)) {
                    // eslint-disable-next-line max-len
                    if (requestKind !== 'salvage') {
                        const referenceNum = _.get(quoteVM, 'referenceNumber');
                        _.set(quoteVM, 'description', referenceNum);
                    }
                    const exclusiveVatMount = calculateEXclVatAmount(value.amount);
                    _.set(quoteVM, 'vat_Excl_EXT', exclusiveVatMount);
                    const vatAmount = calculateVatAmount(value.amount, exclusiveVatMount.amount);
                    _.set(quoteVM, 'vat_EXT', vatAmount);
                }
            } else if (path === 'vat_EXT') {
                const vatInclAmount = _.get(quoteVM, 'vat_incl_EXT.amount.value', 0);
                if (!_.isUndefined(value)) {
                    const exclusiveAmt = calculateVat(value.amount, vatInclAmount);
                    _.set(quoteVM, 'vat_Excl_EXT', exclusiveAmt);
                }
            }
            refreshData();
        },
        [calculateEXclVatAmount, calculateVat, calculateVatAmount, quoteVM, refreshData, requestKind]
    );

    // eslint-disable-next-line no-unused-vars
    const handleVatExempt = useCallback((value, path) => {
        setVatExempt(!showVatExempt);
        const inclVatAmt = _.get(quoteVM, 'vat_incl_EXT');
        // eslint-disable-next-line max-len
        if (inclVatAmt) {
            if (value) {
                const exclusiveVatMount = inclVatAmt;
                // eslint-disable-next-line max-len
                const vatAmt = calculateVatAmount(inclVatAmt.amount.value, exclusiveVatMount.amount.value);
                _.set(quoteVM, 'vat_EXT.value', vatAmt);
                _.set(quoteVM, 'vat_Excl_EXT.value', exclusiveVatMount);
            } else {
                // eslint-disable-next-line max-len
                const exclusiveVatMount = calculateEXclVatAmountForExempt(inclVatAmt.amount.value, value);
                // eslint-disable-next-line max-len
                const vatAmt = calculateVatAmount(inclVatAmt.amount.value, exclusiveVatMount.amount);
                _.set(quoteVM, 'vat_EXT.value', vatAmt);
                _.set(quoteVM, 'vat_Excl_EXT.value', exclusiveVatMount);
            }
        }
        refreshData();
    }, [calculateEXclVatAmountForExempt, calculateVatAmount, quoteVM, refreshData, showVatExempt]);

    const validateDocuments = useCallback(() => {
        const lineItemArray = _.get(quoteVM, 'lineItemData_EXT.value', []);
        const documentsArray = _.get(quoteVM, 'documents.value', []);
        let flag = false;
        if (requestKind === 'salvage') {
            flag = documentsArray.length !== 0
        } else {
            if (quoteAlreadyExists) {
                flag = (lineItemArray.length !== 0) && (documentsArray.length > docNumberPrev);
            } else {
                flag = (lineItemArray.length !== 0) && (documentsArray.length !== 0);
            }
        }
        return flag;
    }, [docNumberPrev, quoteAlreadyExists, quoteVM, requestKind]);



    useEffect(() => registerComponentValidation(validateDocuments), [
        registerComponentValidation, validateDocuments
    ]);

    const handleAddQuote = useCallback(() => {
        onResolve(quoteVM);
    }, [quoteVM, onResolve]);

    const handleClose = useCallback(() => {
        setLineItemVatData([]);
        _.set(quoteVM, 'lineItemData_EXT.value', undefined);
        setTotalInclAmount('');
        onReject();
    }, [onReject, quoteVM]);

    const handleQuoteVMChange = useCallback((newModel) => {
        setQuoteVM(newModel);
        refreshData();
    }, [refreshData]);

    const getModalTitle = () => {
        if (requestKind === 'salvage') {
            return translator(messages.addBid);
        }
        return quoteAlreadyExists ? translator(messages.reviseQuote)
            : translator(messages.addQuote);
    };

    const getFileExtension = useCallback((name) => {
        const extension = name.split('.').pop();
        return extension || '';
    }, []);

    const getMimeType = useCallback((name) => {
        const fileExtension = getFileExtension(name);
        let mimeType = _.get(fileMimeTypeMap, fileExtension);
        if (_.isNil(mimeType)) {
            mimeType = 'application/octet-stream';
        }
        return mimeType;
    }, [fileMimeTypeMap, getFileExtension]);

    const handleUploadDocument = useCallback(async (file) => {
        setLoadingState(true);
        const documentsArray = _.get(quoteVM, 'documents.value', []);
        const fileName = FileUtil.checkFileName(file.name,
            [...existingDocuments, ...documentsArray]);
        const documentMetadataTemplate = {
            DocUID: fileName,
            serviceRequestId: serviceRequestID,
            name: fileName,
            // mimeType: file.type,
            mimeType: getMimeType(file.name),
            DocumentType_EXT: serviceRequestDocumentType,
            visibleToVendor_EXT: true,
            sessionID: await ServiceRequestService.generateUploadToken(authHeader)
        };
        ServiceRequestService.uploadDocument(file, documentMetadataTemplate, authHeader)
            .then((data) => {
                const existingDocumentsToLink = _.get(quoteVM, 'existingDocumentsToLink.value', []);
                _.set(quoteVM, 'documents.value', [...documentsArray, data]);
                _.set(quoteVM, 'existingDocumentsToLink.value', [...existingDocumentsToLink, data.publicID]);
                setQuoteVM(quoteVM);
                refreshData();
            }).catch(() => {
                ModalNextProvider.showAlert({
                    title: messages.error,
                    message: messages.errorMessage,
                    status: 'error',
                    icon: 'mi-error-outline'
                });
            }).finally(() => {
                setLoadingState(false);
            });
    }, [authHeader, existingDocuments, getMimeType, quoteVM, refreshData, serviceRequestDocumentType, serviceRequestID]);

    const handleDeleteDocument = useCallback(async (publicID) => {
        setLoadingState(true);
        await ServiceRequestService.removeDocument_EXT(serviceRequestID, publicID, authHeader)
            .then((isDeleteItem) => {
                if (isDeleteItem) {
                    const uploadedDocuments = _.get(quoteVM, 'documents.value');
                    // eslint-disable-next-line max-len
                    const modifiedDocuments = uploadedDocuments.filter((doc) => doc.publicID !== publicID);
                    _.set(quoteVM, 'documents.value', modifiedDocuments);
                    const uploadedDocLinks = _.get(quoteVM, 'existingDocumentsToLink.value');
                    const modifiedDocLinks = uploadedDocLinks.filter((id) => id !== publicID);
                    _.set(quoteVM, 'existingDocumentsToLink.value', modifiedDocLinks);
                    setQuoteVM(quoteVM);
                    refreshData();
                }
            }).catch(() => {
                ModalNextProvider.showAlert({
                    title: documentMessages.documentDeletionFailed,
                    message: documentMessages.documentCannotDeleted,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: messages.ok
                }).catch(_.noop);
            }).finally(() => {
                setLoadingState(false);
            });
    }, [authHeader, quoteVM, refreshData, serviceRequestID]);

    const getDocumentType = () => {
        switch (serviceRequestDocumentType) {
            case 'Salvage_Tender_Documents':
                return 'Salvage Tender Documents';
            default:
                return serviceRequestDocumentType;
        }
    };

    if (_.isEmpty(quoteVM)) {
        return null;
    }

    const overrides = {
        '@field': {
            showOptional: true
        },
        fileUpload: {
            uploadDocument: handleUploadDocument,
            downloadDocument: ServiceRequestService.downloadDocumentLink,
            isLoading: isLoading
        },
        salvageVatCatagory: {
            visible: (requestKind === 'salvage')
        },
        vatExclAmt: {
            visible: (requestKind === 'salvage')
        },
        lineVateExempt: {
            visible: (requestKind !== 'salvage' && vatCustomer),
            value: showVatExempt,
            onValueChange: handleVatExempt
        },
        lineItemsTable: {
            showSearch: false,
            data: lineItemVatData,
            visible: !_.isEmpty(lineItemVatData)
        },
        addLineButton: {
            onClick: addNewLineItem,
            visible: !(requestKind === 'salvage')
        },
        vatCategory: {
            visible: (requestKind !== 'salvage' && vatCustomer)
        },
        nonSalvageVatExclAmt: {
            visible: (requestKind !== 'salvage' && vatCustomer)
        },
        totalInclAmount: {
            visible: !_.isEmpty(lineItemVatData),
            // eslint-disable-next-line prefer-template
            value: currencyFormat(totalInclAmount)
        },
        serviceRequestDocumentType: {
            value: getDocumentType()
        },
        description: {
            visible: (requestKind === 'salvage')
        },
        lineDescription: {
            visible: (requestKind !== 'salvage')
        },
        documentReqMsg: {
            visible: showDocumentReqMsg
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            getCurrencyExclValue: getCurrencyValue,
            getCurrencyVatValue: getCurrencyValue,
            getCurrencyInclValue: getCurrencyValue
        },
        resolveComponentMap: {
            documentcomponent: DocumentComponent,
        }
    };

    return (
        <ModalNext isOpen={isOpen} onRequestClose={handleClose} onAfterClose={onAfterClose} shouldCloseOnOverlayClick={false}>
            <ModalHeader title={getModalTitle()} />
            <ModalBody>
                <ViewModelForm
                    uiProps={metadata.componentContent.content}
                    model={quoteVM}
                    onValidationChange={onValidate}
                    onModelChange={handleQuoteVMChange}
                    componentMap={resolvers.resolveComponentMap}
                    callbackMap={resolvers.resolveCallbackMap}
                    overrideProps={overrides}
                    onValueChange={writeValue}
                />
            </ModalBody>
            <ModalFooter>
                <Button
                    type="tertiary"
                    onClick={handleClose}
                >
                    {translator(messages.cancel)}
                </Button>
                <Button
                    onClick={handleAddQuote}
                    disabled={!isComponentValid}
                >
                    {translator(messages.save)}
                </Button>
            </ModalFooter>
        </ModalNext>
    );
}

AddQuoteModal.propTypes = {
    quoteValue: PropTypes.shape({
        existingDocumentsToLink: PropTypes.arrayOf({}),
        documents: PropTypes.arrayOf({}),
    }),
    existingDocuments: PropTypes.arrayOf({}),
    serviceRequestID: PropTypes.string,
    // eslint-disable-next-line react/require-default-props
    requestKind: PropTypes.string,
    isOpen: PropTypes.bool,
    onResolve: PropTypes.func,
    onReject: PropTypes.func,
    onAfterClose: PropTypes.func,
    isAddQuote: PropTypes.bool,
    vatCustomer: PropTypes.bool
};

AddQuoteModal.defaultProps = {
    onAfterClose: _.noop,
    isOpen: false,
    onResolve: _.noop,
    onReject: _.noop,
    quoteValue: {},
    existingDocuments: [],
    serviceRequestID: undefined,
    requestKind: undefined,
    isAddQuote: true,
    vatCustomer: false,
    vat_Excl_EXT: undefined,
    description_EXT: undefined,
    vat_EXT: undefined,
    vat_incl_EXT: undefined

};

export default AddQuoteModal;
